import {
  SEVEN_SEGMENT_DEFAULT_COMMON_TYPE,
  SEVEN_SEGMENT_DEFAULT_DIGIT_COUNT,
  SEVEN_SEGMENT_DIGIT_COUNT_OPTIONS,
  SEVEN_SEGMENT_DIGIT_PIN_KEYS,
  SEVEN_SEGMENT_PIN_KEYS,
  SEVEN_SEGMENT_SEGMENT_PIN_KEYS,
  getSevenSegmentDisplayCanvasSize,
} from './sevenSegmentDisplay';
import { BREADBOARD_TEMPLATE_TYPE } from './projectBuilderBreadboard';

const DEFAULT_RESISTOR_VALUE_OPTIONS = [
  { label: '10 ohm', value: 10 },
  { label: '22 ohm', value: 22 },
  { label: '47 ohm', value: 47 },
  { label: '100 ohm', value: 100 },
  { label: '220 ohm', value: 220 },
  { label: '330 ohm', value: 330 },
  { label: '470 ohm', value: 470 },
  { label: '1 kOhm', value: 1000 },
  { label: '2.2 kOhm', value: 2200 },
  { label: '4.7 kOhm', value: 4700 },
  { label: '10 kOhm', value: 10000 },
  { label: '22 kOhm', value: 22000 },
  { label: '47 kOhm', value: 47000 },
  { label: '100 kOhm', value: 100000 },
  { label: '1 MOhm', value: 1000000 },
];

const DEFAULT_POTENTIOMETER_VALUE_OPTIONS = [
  { label: '1 kOhm', value: 1000 },
  { label: '5 kOhm', value: 5000 },
  { label: '10 kOhm', value: 10000 },
  { label: '20 kOhm', value: 20000 },
  { label: '50 kOhm', value: 50000 },
  { label: '100 kOhm', value: 100000 },
];

const POTENTIOMETER_SIMULATION_PARAMETER_DEFAULTS = Object.freeze({
  totalResistanceOhms: 10000,
  position: 0.5,
  minPosition: 0,
  maxPosition: 1,
  taper: 'linear',
});

const VARIABLE_RESISTOR_SIMULATION_PARAMETER_DEFAULTS = Object.freeze({
  maxResistanceOhms: 10000,
  position: 0.5,
  minResistanceOhms: 0,
  taper: 'linear',
});

const SERVO_SIMULATION_PARAMETER_DEFAULTS = Object.freeze({
  idleAngleDegrees: 0,
  maxAngleDegrees: 90,
  minAngleDegrees: -90,
  maxSignalValue: 1,
  maxPulseMicros: 2400,
  minSignalValue: 0,
  minPulseMicros: 544,
  travelDegreesPerSecond: 180,
});

const BUZZER_SIMULATION_PARAMETER_DEFAULTS = Object.freeze({
  minAudibleFrequencyHz: 50,
  maxAudibleFrequencyHz: 8000,
  toneTimeoutMs: 180,
  smoothingSamples: 8,
});

const LCD_16X2_SIMULATION_PARAMETER_DEFAULTS = Object.freeze({
  columns: 16,
  rows: 2,
});

const SEVEN_SEGMENT_SIMULATION_PARAMETER_DEFAULTS = Object.freeze({
  commonType: SEVEN_SEGMENT_DEFAULT_COMMON_TYPE,
  digitCount: SEVEN_SEGMENT_DEFAULT_DIGIT_COUNT,
});

const SEVEN_SEGMENT_SIMULATION_TERMINAL_ROLE_DEFAULTS = Object.freeze({
  ...Object.fromEntries(
    SEVEN_SEGMENT_SEGMENT_PIN_KEYS.map((pinKey) => [
      pinKey,
      `segment_${String(pinKey).toLowerCase()}`,
    ])
  ),
  ...Object.fromEntries(
    SEVEN_SEGMENT_DIGIT_PIN_KEYS.map((pinKey, index) => [
      pinKey,
      `digit_${index + 1}`,
    ])
  ),
});

const GENERIC_2PIN_VARIABLE_RESISTOR_TERMINAL_ROLE_DEFAULTS = Object.freeze({
  1: 'terminal_a',
  2: 'wiper_or_terminal_b',
});

export const PASSIVE_TEMPLATE_OPTIONS = [
  { value: 'resistor', label: 'Resistor' },
  { value: 'push_button', label: 'Push Button' },
  { value: 'potentiometer', label: 'Potentiometer' },
  { value: 'dc_power', label: 'DC Power Source' },
  { value: 'buzzer', label: 'Buzzer' },
  { value: 'servo', label: 'Servo' },
  { value: 'lcd_16x2', label: 'LCD 16x2' },
  { value: 'seven_segment_display', label: '7-Segment Display' },
  { value: BREADBOARD_TEMPLATE_TYPE, label: 'Breadboard' },
  { value: 'generic_2pin', label: 'Generic 2-pin Basic Part' },
  { value: 'generic_3pin', label: 'Generic 3-pin Basic Part' },
];

export const PASSIVE_SIMULATION_BEHAVIOR_OPTIONS = [
  { value: 'none', label: 'No runtime behavior' },
  { value: 'conduction', label: 'Conductive path' },
  { value: 'diode_led', label: 'LED / diode path' },
  { value: 'variable_resistor', label: 'Variable resistor' },
  { value: 'actuator', label: 'Actuator / output load' },
];

export const PASSIVE_SIMULATION_INTERACTION_OPTIONS = [
  { value: 'none', label: 'No interaction' },
  { value: 'momentary_press', label: 'Momentary press' },
  { value: 'toggle', label: 'Toggle switch' },
  { value: 'slider', label: 'Slider / analog position' },
];

export const VARIABLE_RESISTOR_STIMULUS_OUTPUT_OPTIONS = [
  { value: 'position', label: 'Position' },
  { value: 'resistance_ohms', label: 'Resistance (ohms)' },
];

export const PASSIVE_SIMULATION_RULE_CONDITION_OPTIONS = [
  { value: 'always', label: 'Always conductive' },
  { value: 'pressed', label: 'When pressed' },
  { value: 'released', label: 'When released' },
];

const PASSIVE_SIMULATION_BEHAVIOR_SET = new Set(
  PASSIVE_SIMULATION_BEHAVIOR_OPTIONS.map((option) => option.value)
);
const PASSIVE_SIMULATION_INTERACTION_SET = new Set(
  PASSIVE_SIMULATION_INTERACTION_OPTIONS.map((option) => option.value)
);
const VARIABLE_RESISTOR_STIMULUS_OUTPUT_SET = new Set(
  VARIABLE_RESISTOR_STIMULUS_OUTPUT_OPTIONS.map((option) => option.value)
);
const PASSIVE_SIMULATION_RULE_CONDITION_SET = new Set(
  PASSIVE_SIMULATION_RULE_CONDITION_OPTIONS.map((option) => option.value)
);

export const PASSIVE_TEMPLATE_DEFAULTS = {
  resistor: {
    description: 'Two-terminal resistor used for current limiting and pull-up or pull-down wiring.',
    canvasWidth: 80,
    canvasHeight: 18,
    valueOptions: DEFAULT_RESISTOR_VALUE_OPTIONS,
    defaultValue: 10000,
  },
  push_button: {
    description: 'Momentary switch with two terminals.',
    canvasWidth: 92,
    canvasHeight: 68,
    valueOptions: [],
    defaultValue: null,
  },
  potentiometer: {
    description: 'Variable resistor with two end terminals and one adjustable wiper terminal.',
    canvasWidth: 88,
    canvasHeight: 176,
    valueOptions: DEFAULT_POTENTIOMETER_VALUE_OPTIONS,
    defaultValue: 10000,
  },
  dc_power: {
    description: 'Simple 5 V DC source with positive and ground terminals for breadboard lessons.',
    canvasWidth: 92,
    canvasHeight: 64,
    valueOptions: [],
    defaultValue: null,
  },
  buzzer: {
    description: 'Two-pin buzzer or piezo sounder. Connect positive to a signal pin and negative to ground.',
    canvasWidth: 92,
    canvasHeight: 92,
    valueOptions: [],
    defaultValue: null,
  },
  servo: {
    description: 'Three-wire hobby servo with signal, power, and ground terminals. The browser simulator rotates the horn from the Arduino PWM duty cycle.',
    canvasWidth: 180,
    canvasHeight: 68,
    valueOptions: [],
    defaultValue: null,
  },
  lcd_16x2: {
    description: 'HD44780-compatible 16x2 LCD module. The simulator follows 4-bit parallel writes and renders the visible text.',
    canvasWidth: 227,
    canvasHeight: 102,
    valueOptions: [],
    defaultValue: null,
  },
  seven_segment_display: {
    description: 'Multiplexed seven-segment display with shared segment pins (A-G, DP) and per-digit common pins (DIG1-DIG4). Choose common anode or common cathode when adding it to the canvas.',
    canvasWidth: getSevenSegmentDisplayCanvasSize(SEVEN_SEGMENT_DEFAULT_DIGIT_COUNT).width,
    canvasHeight: getSevenSegmentDisplayCanvasSize(SEVEN_SEGMENT_DEFAULT_DIGIT_COUNT).height,
    valueOptions: SEVEN_SEGMENT_DIGIT_COUNT_OPTIONS,
    defaultValue: SEVEN_SEGMENT_DEFAULT_DIGIT_COUNT,
  },
  [BREADBOARD_TEMPLATE_TYPE]: {
    description: 'Full-size solderless breadboard with split terminal columns and continuous power rails for prototyping circuits.',
    canvasWidth: 468,
    canvasHeight: 151,
    valueOptions: [],
    defaultValue: null,
  },
  generic_2pin: {
    description: 'Generic two-pin basic part.',
    canvasWidth: 80,
    canvasHeight: 40,
    valueOptions: [],
    defaultValue: null,
  },
  generic_3pin: {
    description: 'Generic three-pin basic part.',
    canvasWidth: 80,
    canvasHeight: 52,
    valueOptions: [],
    defaultValue: null,
  },
  led: {
    description: 'Light-emitting diode component.',
    canvasWidth: 44,
    canvasHeight: 116,
    valueOptions: [],
    defaultValue: null,
  },
};

export const getPassiveTemplatePinKeys = (templateType) => {
  switch (String(templateType || 'generic_2pin')) {
    case 'resistor':
      return ['A', 'B'];
    case 'push_button':
      return ['1', '2'];
    case 'potentiometer':
      return ['A', 'W', 'B'];
    case 'dc_power':
      return ['+', '-'];
    case 'buzzer':
      return ['+', '-'];
    case 'servo':
      return ['SIG', 'V+', 'GND'];
    case 'lcd_16x2':
      return ['VSS', 'VDD', 'V0', 'RS', 'RW', 'E', 'D0', 'D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'A', 'K'];
    case 'seven_segment_display':
      return SEVEN_SEGMENT_PIN_KEYS;
    case BREADBOARD_TEMPLATE_TYPE:
      return [];
    case 'generic_3pin':
      return ['1', '2', '3'];
    case 'led':
      return ['A', 'K'];
    case 'generic_2pin':
    default:
      return ['1', '2'];
  }
};

export const buildDefaultPassivePinOffsets = (templateType) => (
  Object.fromEntries(
    getPassiveTemplatePinKeys(templateType).map((pinKey) => [
      pinKey,
      { x: 0, y: 0 },
    ])
  )
);

const PASSIVE_SIMULATION_MODEL_PRESETS = {
  resistor: {
    modelKey: 'resistor',
    behavior: 'conduction',
    interaction: 'none',
    terminalRoles: {
      A: 'terminal',
      B: 'terminal',
    },
    conductionRules: [
      { fromPinKey: 'A', toPinKey: 'B', when: 'always' },
    ],
    parameters: {},
    notes: '',
  },
  push_button: {
    modelKey: 'push_button',
    behavior: 'conduction',
    interaction: 'momentary_press',
    terminalRoles: {
      1: 'switch_terminal',
      2: 'switch_terminal',
    },
    conductionRules: [
      { fromPinKey: '1', toPinKey: '2', when: 'pressed' },
    ],
    parameters: {},
    notes: '',
  },
  potentiometer: {
    modelKey: 'potentiometer',
    behavior: 'variable_resistor',
    interaction: 'slider',
    terminalRoles: {
      A: 'end_a',
      W: 'wiper',
      B: 'end_b',
    },
    conductionRules: [],
    parameters: POTENTIOMETER_SIMULATION_PARAMETER_DEFAULTS,
    notes: '3-terminal potentiometer with adjustable wiper position.',
  },
  dc_power: {
    modelKey: 'dc_power',
    behavior: 'none',
    interaction: 'none',
    terminalRoles: {
      '+': 'positive',
      '-': 'ground',
    },
    conductionRules: [],
    parameters: {
      voltage: 5,
    },
    notes: 'Ideal lesson supply. Positive and ground are classified as separate power nets.',
  },
  buzzer: {
    modelKey: 'buzzer',
    behavior: 'actuator',
    interaction: 'none',
    terminalRoles: {
      '+': 'positive',
      '-': 'negative',
    },
    conductionRules: [],
    parameters: BUZZER_SIMULATION_PARAMETER_DEFAULTS,
    notes: 'Passive buzzer / piezo load. The simulator estimates tone frequency from GPIO pin transitions.',
  },
  servo: {
    modelKey: 'servo',
    behavior: 'actuator',
    interaction: 'none',
    terminalRoles: {
      SIG: 'signal',
      'V+': 'power',
      GND: 'ground',
    },
    conductionRules: [],
    parameters: SERVO_SIMULATION_PARAMETER_DEFAULTS,
    notes: 'Maps Arduino PWM duty cycle to the visible servo horn rotation.',
  },
  lcd_16x2: {
    modelKey: 'lcd_16x2',
    behavior: 'actuator',
    interaction: 'none',
    terminalRoles: {
      VSS: 'ground',
      VDD: 'power',
      V0: 'contrast',
      RS: 'register_select',
      RW: 'read_write',
      E: 'enable',
      D0: 'data_bit_0',
      D1: 'data_bit_1',
      D2: 'data_bit_2',
      D3: 'data_bit_3',
      D4: 'data_bit_4',
      D5: 'data_bit_5',
      D6: 'data_bit_6',
      D7: 'data_bit_7',
      A: 'backlight_anode',
      K: 'backlight_cathode',
    },
    conductionRules: [],
    parameters: LCD_16X2_SIMULATION_PARAMETER_DEFAULTS,
    notes: 'Decodes HD44780-compatible 4-bit parallel writes and renders the 16x2 character display in the browser simulator.',
  },
  seven_segment_display: {
    modelKey: 'seven_segment_display',
    behavior: 'actuator',
    interaction: 'none',
    terminalRoles: SEVEN_SEGMENT_SIMULATION_TERMINAL_ROLE_DEFAULTS,
    conductionRules: [],
    parameters: SEVEN_SEGMENT_SIMULATION_PARAMETER_DEFAULTS,
    notes: 'Supports common-cathode and common-anode multiplexed displays. The simulator resolves visible segments from the connected pin drive states; driver/protocol decoding is not implemented.',
  },
  generic_2pin: {
    modelKey: 'generic_2pin',
    behavior: 'none',
    interaction: 'none',
    terminalRoles: {
      1: 'terminal',
      2: 'terminal',
    },
    conductionRules: [],
    parameters: {},
    notes: '',
  },
  generic_3pin: {
    modelKey: 'generic_3pin',
    behavior: 'none',
    interaction: 'none',
    terminalRoles: {
      1: 'terminal',
      2: 'terminal',
      3: 'terminal',
    },
    conductionRules: [],
    parameters: {},
    notes: '',
  },
  [BREADBOARD_TEMPLATE_TYPE]: {
    modelKey: BREADBOARD_TEMPLATE_TYPE,
    behavior: 'none',
    interaction: 'none',
    terminalRoles: {},
    conductionRules: [],
    parameters: {},
    notes: 'Breadboard bus connectivity is resolved from the hole layout in Project Builder.',
  },
  led: {
    modelKey: 'led',
    behavior: 'diode_led',
    interaction: 'none',
    terminalRoles: {
      A: 'anode',
      K: 'cathode',
    },
    conductionRules: [],
    parameters: {},
    notes: '',
  },
};

const cloneSimulationModel = (value) => (
  JSON.parse(JSON.stringify(value))
);

const VARIABLE_RESISTOR_FORMULA_CONSTANTS = Object.freeze({
  e: Math.E,
  pi: Math.PI,
});

const VARIABLE_RESISTOR_FORMULA_FUNCTIONS = Object.freeze({
  abs: (value) => Math.abs(Number(value)),
  ceil: (value) => Math.ceil(Number(value)),
  clamp: (value, minimum, maximum) => {
    const numericValue = Number(value);
    const numericMinimum = Number(minimum);
    const numericMaximum = Number(maximum);

    if (
      !Number.isFinite(numericValue) ||
      !Number.isFinite(numericMinimum) ||
      !Number.isFinite(numericMaximum)
    ) {
      throw new Error('Formula clamp() arguments must be finite numbers.');
    }

    const lowerBound = Math.min(numericMinimum, numericMaximum);
    const upperBound = Math.max(numericMinimum, numericMaximum);
    return Math.min(upperBound, Math.max(lowerBound, numericValue));
  },
  exp: (value) => Math.exp(Number(value)),
  floor: (value) => Math.floor(Number(value)),
  log: (value) => {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      throw new Error('Formula log() input must be greater than zero.');
    }

    return Math.log(numericValue);
  },
  max: (...values) => {
    if (values.length === 0) {
      throw new Error('Formula max() requires at least one argument.');
    }

    const numericValues = values.map((value) => Number(value));

    if (numericValues.some((value) => !Number.isFinite(value))) {
      throw new Error('Formula max() arguments must be finite numbers.');
    }

    return Math.max(...numericValues);
  },
  min: (...values) => {
    if (values.length === 0) {
      throw new Error('Formula min() requires at least one argument.');
    }

    const numericValues = values.map((value) => Number(value));

    if (numericValues.some((value) => !Number.isFinite(value))) {
      throw new Error('Formula min() arguments must be finite numbers.');
    }

    return Math.min(...numericValues);
  },
  pow: (base, exponent) => Math.pow(Number(base), Number(exponent)),
  round: (value) => Math.round(Number(value)),
  sqrt: (value) => {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue) || numericValue < 0) {
      throw new Error('Formula sqrt() input cannot be negative.');
    }

    return Math.sqrt(numericValue);
  },
});

const VARIABLE_RESISTOR_FORMULA_SAMPLE_SCOPE = Object.freeze({
  maxPosition: 1,
  maxResistanceOhms: 100000,
  minPosition: 0,
  minResistanceOhms: 1000,
  normalizedStimulus: 0.5,
  positionRange: 1,
  resistanceRangeOhms: 99000,
  stimulus: 500,
  stimulusMax: 1000,
  stimulusMin: 0,
  stimulusRange: 1000,
  stimulusValue: 500,
});

const clampPassiveNumber = (value, minimum, maximum) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return minimum;
  }

  return Math.min(maximum, Math.max(minimum, numericValue));
};

const getFinitePassiveNumber = (value, fallbackValue) => {
  if (value === null || value === undefined || value === '') {
    return fallbackValue;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallbackValue;
};

const normalizePassiveBoolean = (value, fallbackValue = false) => {
  if (value === undefined || value === null || value === '') {
    return Boolean(fallbackValue);
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  const normalizedValue = String(value).trim().toLowerCase();

  if (['1', 'true', 'yes', 'on'].includes(normalizedValue)) {
    return true;
  }

  if (['0', 'false', 'no', 'off'].includes(normalizedValue)) {
    return false;
  }

  return Boolean(fallbackValue);
};

const normalizePassiveShortString = (value, maxLength = 120) => {
  const normalizedValue = String(value ?? '').trim();

  if (!normalizedValue) {
    return '';
  }

  return normalizedValue.slice(0, maxLength);
};

const tokenizeVariableResistorStimulusFormula = (formula) => {
  const tokens = [];
  const source = String(formula || '');
  let cursor = 0;

  while (cursor < source.length) {
    const currentCharacter = source[cursor];

    if (/\s/.test(currentCharacter)) {
      cursor += 1;
      continue;
    }

    const numberMatch = source.slice(cursor).match(/^(?:\d+\.\d*|\d+|\.\d+)(?:[eE][+-]?\d+)?/);

    if (numberMatch) {
      tokens.push({
        type: 'number',
        value: Number(numberMatch[0]),
      });
      cursor += numberMatch[0].length;
      continue;
    }

    const identifierMatch = source.slice(cursor).match(/^[A-Za-z_][A-Za-z0-9_]*/);

    if (identifierMatch) {
      tokens.push({
        type: 'identifier',
        value: identifierMatch[0],
      });
      cursor += identifierMatch[0].length;
      continue;
    }

    if ('+-*/%^(),'.includes(currentCharacter)) {
      tokens.push({
        type: currentCharacter === '(' || currentCharacter === ')'
          ? 'paren'
          : currentCharacter === ','
            ? 'comma'
            : 'operator',
        value: currentCharacter,
      });
      cursor += 1;
      continue;
    }

    throw new Error(`Unsupported formula token "${currentCharacter}".`);
  }

  return tokens;
};

const evaluateVariableResistorStimulusFormula = (formula, scope = {}) => {
  const trimmedFormula = String(formula || '').trim();

  if (!trimmedFormula) {
    return null;
  }

  const tokens = tokenizeVariableResistorStimulusFormula(trimmedFormula);

  if (tokens.length === 0) {
    return null;
  }

  let cursor = 0;

  const peekToken = () => tokens[cursor] || null;
  const consumeToken = () => {
    const token = tokens[cursor] || null;
    cursor += 1;
    return token;
  };

  const expectToken = (type, value = null) => {
    const token = consumeToken();

    if (!token || token.type !== type || (value !== null && token.value !== value)) {
      const expectedValue = value === null ? type : `${type} "${value}"`;
      throw new Error(`Expected ${expectedValue} in formula.`);
    }

    return token;
  };

  const parseExpression = () => parseAdditive();

  const parseAdditive = () => {
    let currentValue = parseMultiplicative();

    while (true) {
      const token = peekToken();

      if (!token || token.type !== 'operator' || !['+', '-'].includes(token.value)) {
        return currentValue;
      }

      consumeToken();
      const nextValue = parseMultiplicative();
      currentValue = token.value === '+'
        ? currentValue + nextValue
        : currentValue - nextValue;
    }
  };

  const parseMultiplicative = () => {
    let currentValue = parsePower();

    while (true) {
      const token = peekToken();

      if (!token || token.type !== 'operator' || !['*', '/', '%'].includes(token.value)) {
        return currentValue;
      }

      consumeToken();
      const nextValue = parsePower();

      if (token.value === '*') {
        currentValue *= nextValue;
      } else if (token.value === '/') {
        if (Math.abs(nextValue) <= Number.EPSILON) {
          throw new Error('Formula cannot divide by zero.');
        }

        currentValue /= nextValue;
      } else {
        if (Math.abs(nextValue) <= Number.EPSILON) {
          throw new Error('Formula cannot use modulo by zero.');
        }

        currentValue %= nextValue;
      }
    }
  };

  const parsePower = () => {
    const baseValue = parseUnary();
    const token = peekToken();

    if (!token || token.type !== 'operator' || token.value !== '^') {
      return baseValue;
    }

    consumeToken();
    return Math.pow(baseValue, parsePower());
  };

  const parseUnary = () => {
    const token = peekToken();

    if (!token || token.type !== 'operator' || !['+', '-'].includes(token.value)) {
      return parsePrimary();
    }

    consumeToken();
    const unaryValue = parseUnary();
    return token.value === '-' ? -unaryValue : unaryValue;
  };

  const parsePrimary = () => {
    const token = peekToken();

    if (!token) {
      throw new Error('Formula ended unexpectedly.');
    }

    if (token.type === 'number') {
      consumeToken();
      return token.value;
    }

    if (token.type === 'identifier') {
      consumeToken();
      const nextToken = peekToken();

      if (nextToken?.type === 'paren' && nextToken.value === '(') {
        consumeToken();
        const args = [];

        if (!(peekToken()?.type === 'paren' && peekToken()?.value === ')')) {
          while (true) {
            args.push(parseExpression());

            if (peekToken()?.type === 'comma') {
              consumeToken();
              continue;
            }

            break;
          }
        }

        expectToken('paren', ')');
        const functionName = String(token.value || '').trim().toLowerCase();
        const formulaFunction = VARIABLE_RESISTOR_FORMULA_FUNCTIONS[functionName];

        if (typeof formulaFunction !== 'function') {
          throw new Error(`Unsupported formula function "${token.value}".`);
        }

        const functionResult = formulaFunction(...args);

        if (!Number.isFinite(functionResult)) {
          throw new Error(`Formula function "${token.value}" did not return a finite number.`);
        }

        return functionResult;
      }

      if (Object.prototype.hasOwnProperty.call(scope, token.value)) {
        const scopeValue = Number(scope[token.value]);

        if (!Number.isFinite(scopeValue)) {
          throw new Error(`Formula variable "${token.value}" is not numeric.`);
        }

        return scopeValue;
      }

      const constantValue = VARIABLE_RESISTOR_FORMULA_CONSTANTS[
        String(token.value || '').trim().toLowerCase()
      ];

      if (Number.isFinite(constantValue)) {
        return constantValue;
      }

      throw new Error(`Unsupported formula variable "${token.value}".`);
    }

    if (token.type === 'paren' && token.value === '(') {
      consumeToken();
      const innerValue = parseExpression();
      expectToken('paren', ')');
      return innerValue;
    }

    throw new Error(`Unexpected token "${token.value}" in formula.`);
  };

  const result = parseExpression();

  if (cursor < tokens.length) {
    const trailingToken = tokens[cursor];
    throw new Error(`Unexpected trailing token "${trailingToken?.value || ''}" in formula.`);
  }

  if (!Number.isFinite(result)) {
    throw new Error('Formula must resolve to a finite number.');
  }

  return result;
};

export const validateVariableResistorStimulusFormula = (formula) => {
  const trimmedFormula = String(formula || '').trim();

  if (!trimmedFormula) {
    return '';
  }

  try {
    evaluateVariableResistorStimulusFormula(
      trimmedFormula,
      VARIABLE_RESISTOR_FORMULA_SAMPLE_SCOPE
    );
    return '';
  } catch (error) {
    return error?.message || 'Stimulus formula is invalid.';
  }
};

const isGeneric2PinVariableResistorProfile = ({
  templateType,
  modelKey,
  behavior,
}) => (
  String(templateType || 'generic_2pin') === 'generic_2pin' &&
  (
    String(modelKey || '').trim() === 'variable_resistor' ||
    String(behavior || '').trim() === 'variable_resistor'
  )
);

const resolvePassiveSimulationModelDefaults = (templateType, value) => {
  const normalizedTemplateType = String(templateType || 'generic_2pin');
  const defaults = buildDefaultPassiveSimulationModel(normalizedTemplateType);
  const modelKey = String(value?.modelKey ?? value?.model_key ?? '').trim();
  const behavior = String(value?.behavior ?? '').trim();

  if (isGeneric2PinVariableResistorProfile({
    templateType: normalizedTemplateType,
    modelKey,
    behavior,
  })) {
    return {
      ...defaults,
      modelKey: 'variable_resistor',
      behavior: 'variable_resistor',
      interaction: 'slider',
      terminalRoles: GENERIC_2PIN_VARIABLE_RESISTOR_TERMINAL_ROLE_DEFAULTS,
      conductionRules: [],
      parameters: cloneSimulationModel(VARIABLE_RESISTOR_SIMULATION_PARAMETER_DEFAULTS),
      notes: '2-terminal variable resistor.',
    };
  }

  return defaults;
};

const normalizePassiveSimulationParameters = (value) => {
  if (typeof value === 'string') {
    try {
      return normalizePassiveSimulationParameters(JSON.parse(value));
    } catch {
      return {};
    }
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return cloneSimulationModel(value);
};

const mergePassiveSimulationParameters = (defaults, overrides) => ({
  ...normalizePassiveSimulationParameters(defaults),
  ...normalizePassiveSimulationParameters(overrides),
});

export const variableResistorHasStimulusControl = (parameters = {}) => (
  normalizePassiveBoolean(parameters?.stimulusEnabled, false)
);

const hasVariableResistorStimulusMetadata = (parameters = {}) => (
  [
    'stimulusEnabled',
    'stimulusFormula',
    'stimulusInvert',
    'stimulusLabel',
    'stimulusMax',
    'stimulusMin',
    'stimulusOutputMode',
    'stimulusStep',
    'stimulusUnit',
    'stimulusValue',
  ].some((key) => Object.prototype.hasOwnProperty.call(parameters, key))
);

const normalizeVariableResistorSimulationParameters = (parameters = {}) => {
  const rawParameters = normalizePassiveSimulationParameters(parameters);
  const normalizedParameters = {
    ...rawParameters,
  };
  const minimumPosition = getFinitePassiveNumber(rawParameters.minPosition, 0);
  const maximumPosition = getFinitePassiveNumber(rawParameters.maxPosition, 1);
  const lowerPosition = Math.min(minimumPosition, maximumPosition);
  const upperPosition = Math.max(minimumPosition, maximumPosition);
  const defaultPosition = lowerPosition + ((upperPosition - lowerPosition) / 2);
  const position = clampPassiveNumber(
    getFinitePassiveNumber(rawParameters.position, defaultPosition),
    lowerPosition,
    upperPosition
  );
  const rawMaximumResistance = getFinitePassiveNumber(
    rawParameters.totalResistanceOhms,
    getFinitePassiveNumber(rawParameters.maxResistanceOhms, 10000)
  );
  const rawMinimumResistance = Math.max(
    getFinitePassiveNumber(rawParameters.minResistanceOhms, 0),
    0
  );
  const lowerResistance = Math.min(rawMinimumResistance, rawMaximumResistance);
  const upperResistance = Math.max(rawMinimumResistance, rawMaximumResistance);

  normalizedParameters.minPosition = lowerPosition;
  normalizedParameters.maxPosition = upperPosition;
  normalizedParameters.position = position;
  normalizedParameters.minResistanceOhms = lowerResistance;
  normalizedParameters.taper = normalizePassiveShortString(rawParameters.taper || 'linear', 40) || 'linear';

  if (
    Object.prototype.hasOwnProperty.call(rawParameters, 'totalResistanceOhms') ||
    !Object.prototype.hasOwnProperty.call(rawParameters, 'maxResistanceOhms')
  ) {
    normalizedParameters.totalResistanceOhms = upperResistance;
  }

  if (
    Object.prototype.hasOwnProperty.call(rawParameters, 'maxResistanceOhms') ||
    !Object.prototype.hasOwnProperty.call(rawParameters, 'totalResistanceOhms')
  ) {
    normalizedParameters.maxResistanceOhms = upperResistance;
  }

  if (!hasVariableResistorStimulusMetadata(rawParameters)) {
    return normalizedParameters;
  }

  const stimulusMinimum = getFinitePassiveNumber(rawParameters.stimulusMin, 0);
  const stimulusMaximum = getFinitePassiveNumber(rawParameters.stimulusMax, 1);
  const lowerStimulus = Math.min(stimulusMinimum, stimulusMaximum);
  const upperStimulus = Math.max(stimulusMinimum, stimulusMaximum);
  const stimulusRange = upperStimulus - lowerStimulus;
  const defaultStimulusValue = lowerStimulus + (stimulusRange / 2);
  const normalizedStep = getFinitePassiveNumber(rawParameters.stimulusStep, null);

  normalizedParameters.stimulusEnabled = variableResistorHasStimulusControl(rawParameters);
  normalizedParameters.stimulusLabel = normalizePassiveShortString(rawParameters.stimulusLabel, 80);
  normalizedParameters.stimulusUnit = normalizePassiveShortString(rawParameters.stimulusUnit, 40);
  normalizedParameters.stimulusMin = lowerStimulus;
  normalizedParameters.stimulusMax = upperStimulus;
  normalizedParameters.stimulusStep = normalizedStep !== null && normalizedStep > 0
    ? normalizedStep
    : (stimulusRange > 0 ? Math.max(stimulusRange / 100, 0.001) : 0.01);
  normalizedParameters.stimulusValue = clampPassiveNumber(
    getFinitePassiveNumber(rawParameters.stimulusValue, defaultStimulusValue),
    lowerStimulus,
    upperStimulus
  );
  normalizedParameters.stimulusOutputMode = VARIABLE_RESISTOR_STIMULUS_OUTPUT_SET.has(
    String(rawParameters.stimulusOutputMode || '').trim()
  )
    ? String(rawParameters.stimulusOutputMode).trim()
    : 'position';
  normalizedParameters.stimulusInvert = normalizePassiveBoolean(rawParameters.stimulusInvert, false);
  normalizedParameters.stimulusFormula = normalizePassiveShortString(rawParameters.stimulusFormula, 500);

  return normalizedParameters;
};

export const resolveVariableResistorSimulationState = (
  parameters = {},
  {
    totalResistanceOhms: totalResistanceOverride = null,
  } = {}
) => {
  const normalizedParameters = normalizeVariableResistorSimulationParameters(parameters);
  const minimumPosition = getFinitePassiveNumber(normalizedParameters.minPosition, 0);
  const maximumPosition = getFinitePassiveNumber(normalizedParameters.maxPosition, 1);
  const lowerPosition = Math.min(minimumPosition, maximumPosition);
  const upperPosition = Math.max(minimumPosition, maximumPosition);
  const positionRange = upperPosition - lowerPosition;
  const defaultPosition = lowerPosition + (positionRange / 2);
  const basePosition = clampPassiveNumber(
    getFinitePassiveNumber(normalizedParameters.position, defaultPosition),
    lowerPosition,
    upperPosition
  );
  const resolvedMaximumResistance = getFinitePassiveNumber(
    totalResistanceOverride,
    getFinitePassiveNumber(
      normalizedParameters.totalResistanceOhms,
      getFinitePassiveNumber(normalizedParameters.maxResistanceOhms, 10000)
    )
  );
  const resolvedMinimumResistance = getFinitePassiveNumber(
    normalizedParameters.minResistanceOhms,
    0
  );
  const lowerResistance = Math.min(resolvedMinimumResistance, resolvedMaximumResistance);
  const upperResistance = Math.max(resolvedMinimumResistance, resolvedMaximumResistance);
  const resistanceRange = upperResistance - lowerResistance;
  const usesStimulusControl = variableResistorHasStimulusControl(normalizedParameters);
  const stimulusMinimum = getFinitePassiveNumber(normalizedParameters.stimulusMin, 0);
  const stimulusMaximum = getFinitePassiveNumber(normalizedParameters.stimulusMax, 1);
  const lowerStimulus = Math.min(stimulusMinimum, stimulusMaximum);
  const upperStimulus = Math.max(stimulusMinimum, stimulusMaximum);
  const stimulusRange = upperStimulus - lowerStimulus;
  const defaultStimulusValue = lowerStimulus + (stimulusRange / 2);
  const stimulusValue = clampPassiveNumber(
    getFinitePassiveNumber(normalizedParameters.stimulusValue, defaultStimulusValue),
    lowerStimulus,
    upperStimulus
  );
  const normalizedStimulus = stimulusRange > 0
    ? clampPassiveNumber((stimulusValue - lowerStimulus) / stimulusRange, 0, 1)
    : 0.5;
  const linearStimulusRatio = normalizedParameters.stimulusInvert
    ? 1 - normalizedStimulus
    : normalizedStimulus;
  let resolvedPosition = basePosition;
  let resolvedResistanceOhms = lowerResistance + (
    (upperResistance - lowerResistance) *
    (positionRange > 0
      ? clampPassiveNumber((basePosition - lowerPosition) / positionRange, 0, 1)
      : 0.5)
  );
  let formulaError = '';
  let resolvedByFormula = false;

  if (usesStimulusControl) {
    resolvedPosition = lowerPosition + (positionRange * linearStimulusRatio);
    resolvedResistanceOhms = lowerResistance + (resistanceRange * linearStimulusRatio);

    const trimmedFormula = String(normalizedParameters.stimulusFormula || '').trim();

    if (trimmedFormula) {
      try {
        const formulaScope = {
          maxPosition: upperPosition,
          maxResistanceOhms: upperResistance,
          minPosition: lowerPosition,
          minResistanceOhms: lowerResistance,
          normalizedStimulus,
          positionRange,
          resistanceRangeOhms: resistanceRange,
          stimulus: stimulusValue,
          stimulusMax: upperStimulus,
          stimulusMin: lowerStimulus,
          stimulusRange,
          stimulusValue,
        };
        const formulaResult = evaluateVariableResistorStimulusFormula(
          trimmedFormula,
          formulaScope
        );

        if (normalizedParameters.stimulusOutputMode === 'resistance_ohms') {
          resolvedResistanceOhms = clampPassiveNumber(
            formulaResult,
            lowerResistance,
            upperResistance
          );
          const resistanceRatio = resistanceRange > 0
            ? clampPassiveNumber(
                (resolvedResistanceOhms - lowerResistance) / resistanceRange,
                0,
                1
              )
            : 0.5;
          resolvedPosition = lowerPosition + (positionRange * resistanceRatio);
        } else {
          resolvedPosition = clampPassiveNumber(
            formulaResult,
            lowerPosition,
            upperPosition
          );
          const positionRatio = positionRange > 0
            ? clampPassiveNumber(
                (resolvedPosition - lowerPosition) / positionRange,
                0,
                1
              )
            : 0.5;
          resolvedResistanceOhms = lowerResistance + (resistanceRange * positionRatio);
        }

        resolvedByFormula = true;
      } catch (error) {
        formulaError = error?.message || 'Stimulus formula is invalid.';
      }
    }
  }

  const resolvedPositionRatio = positionRange > 0
    ? clampPassiveNumber((resolvedPosition - lowerPosition) / positionRange, 0, 1)
    : 0.5;

  return {
    controlMax: usesStimulusControl ? upperStimulus : upperPosition,
    controlMin: usesStimulusControl ? lowerStimulus : lowerPosition,
    controlMode: usesStimulusControl ? 'stimulus' : 'position',
    controlPercent: Math.round((usesStimulusControl ? normalizedStimulus : resolvedPositionRatio) * 100),
    controlStep: usesStimulusControl
      ? Math.max(getFinitePassiveNumber(normalizedParameters.stimulusStep, 0.01), 0.001)
      : (positionRange > 0 ? Math.max(positionRange / 100, 0.001) : 0.01),
    controlValue: usesStimulusControl ? stimulusValue : resolvedPosition,
    formulaError,
    hasStimulusControl: usesStimulusControl,
    maxPosition: upperPosition,
    maxResistanceOhms: upperResistance,
    minPosition: lowerPosition,
    minResistanceOhms: lowerResistance,
    normalizedStimulus,
    position: resolvedPosition,
    positionRatio: resolvedPositionRatio,
    resolvedByFormula,
    resolvedResistanceOhms,
    stimulusFormula: String(normalizedParameters.stimulusFormula || '').trim(),
    stimulusInvert: normalizePassiveBoolean(normalizedParameters.stimulusInvert, false),
    stimulusLabel: normalizePassiveShortString(normalizedParameters.stimulusLabel, 80),
    stimulusMax: upperStimulus,
    stimulusMin: lowerStimulus,
    stimulusOutputMode: normalizedParameters.stimulusOutputMode === 'resistance_ohms'
      ? 'resistance_ohms'
      : 'position',
    stimulusStep: Math.max(getFinitePassiveNumber(normalizedParameters.stimulusStep, 0.01), 0.001),
    stimulusUnit: normalizePassiveShortString(normalizedParameters.stimulusUnit, 40),
    stimulusValue,
    taper: normalizePassiveShortString(normalizedParameters.taper || 'linear', 40) || 'linear',
    totalResistanceOhms: upperResistance,
  };
};

export const buildDefaultPassiveSimulationModel = (templateType) => {
  const resolvedTemplateType = String(templateType || 'generic_2pin');
  const templatePinKeys = getPassiveTemplatePinKeys(resolvedTemplateType);
  const basePreset = cloneSimulationModel(
    PASSIVE_SIMULATION_MODEL_PRESETS[resolvedTemplateType] ||
    PASSIVE_SIMULATION_MODEL_PRESETS.generic_2pin
  );

  basePreset.terminalRoles = Object.fromEntries(
    templatePinKeys.map((pinKey) => [
      pinKey,
      String(basePreset.terminalRoles?.[pinKey] || 'terminal').trim() || 'terminal',
    ])
  );

  basePreset.conductionRules = (Array.isArray(basePreset.conductionRules)
    ? basePreset.conductionRules
    : []
  ).filter((rule) => (
    templatePinKeys.includes(rule?.fromPinKey) &&
    templatePinKeys.includes(rule?.toPinKey)
  ));

  return basePreset;
};

export const normalizePassiveSimulationModel = (value, templateType) => {
  const normalizedTemplateType = String(templateType || 'generic_2pin');
  const templatePinKeys = getPassiveTemplatePinKeys(normalizedTemplateType);
  let rawValue = value;

  if (typeof rawValue === 'string') {
    try {
      rawValue = JSON.parse(rawValue);
    } catch {
      rawValue = {};
    }
  }

  if (!rawValue || typeof rawValue !== 'object' || Array.isArray(rawValue)) {
    rawValue = {};
  }

  const defaults = resolvePassiveSimulationModelDefaults(
    normalizedTemplateType,
    rawValue
  );

  const behavior = String(rawValue.behavior || defaults.behavior).trim();
  const interaction = String(rawValue.interaction || defaults.interaction).trim();
  const rawParameters =
    rawValue.parameters !== undefined
      ? rawValue.parameters
      : rawValue.metadata !== undefined
        ? rawValue.metadata
        : {};
  const rawNotes =
    rawValue.notes === undefined || rawValue.notes === null
      ? defaults.notes
      : rawValue.notes;
  const usesGeneric2PinVariableResistorProfile = isGeneric2PinVariableResistorProfile({
    templateType: normalizedTemplateType,
    modelKey: rawValue.modelKey ?? rawValue.model_key ?? defaults.modelKey,
    behavior,
  });
  const normalizedTerminalRoles = Object.fromEntries(
    templatePinKeys.map((pinKey) => [
      pinKey,
      String(
        rawValue.terminalRoles?.[pinKey] ??
        rawValue.terminal_roles?.[pinKey] ??
        defaults.terminalRoles?.[pinKey] ??
        'terminal'
      ).trim() || 'terminal',
    ])
  );
  const resolvedModelKey = usesGeneric2PinVariableResistorProfile
    ? 'variable_resistor'
    : (String(rawValue.modelKey ?? rawValue.model_key ?? defaults.modelKey).trim() || defaults.modelKey);
  const resolvedBehavior = usesGeneric2PinVariableResistorProfile
    ? 'variable_resistor'
    : (PASSIVE_SIMULATION_BEHAVIOR_SET.has(behavior) ? behavior : defaults.behavior);
  const resolvedInteraction = usesGeneric2PinVariableResistorProfile
    ? 'slider'
    : (PASSIVE_SIMULATION_INTERACTION_SET.has(interaction) ? interaction : defaults.interaction);
  const mergedParameters = mergePassiveSimulationParameters(defaults.parameters, rawParameters);
  const normalizedParameters = (
    normalizedTemplateType === 'potentiometer' ||
    resolvedModelKey === 'potentiometer' ||
    resolvedModelKey === 'variable_resistor' ||
    resolvedBehavior === 'variable_resistor'
  )
    ? normalizeVariableResistorSimulationParameters(mergedParameters)
    : mergedParameters;

  return {
    modelKey: resolvedModelKey,
    behavior: resolvedBehavior,
    interaction: resolvedInteraction,
    terminalRoles: usesGeneric2PinVariableResistorProfile
      ? Object.fromEntries(
          templatePinKeys.map((pinKey) => {
            const normalizedRole = normalizedTerminalRoles[pinKey];

            return [
              pinKey,
              normalizedRole === 'terminal'
                ? GENERIC_2PIN_VARIABLE_RESISTOR_TERMINAL_ROLE_DEFAULTS[pinKey] || normalizedRole
                : normalizedRole,
            ];
          })
        )
      : normalizedTerminalRoles,
    conductionRules: (Array.isArray(rawValue.conductionRules)
      ? rawValue.conductionRules
      : Array.isArray(rawValue.conduction_rules)
        ? rawValue.conduction_rules
        : defaults.conductionRules
    )
      .map((rule) => ({
        fromPinKey: String(rule?.fromPinKey ?? rule?.from_pin_key ?? '').trim(),
        toPinKey: String(rule?.toPinKey ?? rule?.to_pin_key ?? '').trim(),
        when: String(rule?.when || 'always').trim() || 'always',
      }))
      .filter((rule) => (
        templatePinKeys.includes(rule.fromPinKey) &&
        templatePinKeys.includes(rule.toPinKey) &&
        PASSIVE_SIMULATION_RULE_CONDITION_SET.has(rule.when)
      )),
    parameters: normalizedParameters,
    notes: String(rawNotes ?? '').trim(),
  };
};

export const resolveRuntimePassiveSimulationModel = (value, templateType) => {
  const normalizedTemplateType = String(templateType || 'generic_2pin');
  const normalizedModel = normalizePassiveSimulationModel(value, normalizedTemplateType);
  const needsPotentiometerInference = (
    normalizedTemplateType === 'potentiometer' &&
    (
      normalizedModel.behavior === 'none' ||
      normalizedModel.interaction === 'none' ||
      normalizedModel.modelKey === 'generic_3pin' ||
      normalizedModel.modelKey === 'generic_2pin'
    )
  );
  const needsVariableResistorInference = (
    normalizedTemplateType === 'generic_2pin' &&
    (
      normalizedModel.modelKey === 'variable_resistor' ||
      normalizedModel.behavior === 'variable_resistor'
    ) &&
    normalizedModel.interaction === 'none'
  );

  if (!needsPotentiometerInference && !needsVariableResistorInference) {
    return normalizedModel;
  }

  return normalizePassiveSimulationModel(
    {
      ...normalizedModel,
      modelKey: needsPotentiometerInference ? 'potentiometer' : 'variable_resistor',
      behavior: 'variable_resistor',
      interaction: 'slider',
    },
    normalizedTemplateType
  );
};

export const resolvePassiveComponentRuntimeSimulationModel = (
  component,
  passiveDefinition = null
) => {
  const templateType = String(
    component?.passiveType ||
    passiveDefinition?.templateType ||
    'generic_2pin'
  );
  const definitionRuntimeModel = passiveDefinition?.simulationModel
    ? resolveRuntimePassiveSimulationModel(passiveDefinition.simulationModel, templateType)
    : null;
  const componentRuntimeModel = component?.simulationModel
    ? resolveRuntimePassiveSimulationModel(component.simulationModel, templateType)
    : null;
  const runtimeModel = definitionRuntimeModel ||
    componentRuntimeModel ||
    resolveRuntimePassiveSimulationModel({}, templateType);

  if (
    String(runtimeModel?.interaction || '').trim() !== 'slider' ||
    !componentRuntimeModel?.parameters
  ) {
    return runtimeModel;
  }

  const nextParameters = {
    ...(runtimeModel.parameters || {}),
  };
  let didOverrideDynamicParameter = false;

  ['position', 'stimulusValue'].forEach((parameterKey) => {
    if (Object.prototype.hasOwnProperty.call(componentRuntimeModel.parameters, parameterKey)) {
      nextParameters[parameterKey] = componentRuntimeModel.parameters[parameterKey];
      didOverrideDynamicParameter = true;
    }
  });

  if (!didOverrideDynamicParameter) {
    return runtimeModel;
  }

  return {
    ...runtimeModel,
    parameters: nextParameters,
  };
};

export const normalizePassivePinOffsets = (value, templateType) => {
  if (typeof value === 'string') {
    try {
      return normalizePassivePinOffsets(JSON.parse(value), templateType);
    } catch {
      return buildDefaultPassivePinOffsets(templateType);
    }
  }

  const defaults = buildDefaultPassivePinOffsets(templateType);

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return defaults;
  }

  return Object.fromEntries(
    Object.keys(defaults).map((pinKey) => {
      const rawOffset = value[pinKey];
      const rawX = rawOffset?.x ?? 0;
      const rawY = rawOffset?.y ?? 0;
      const numericX = Number(rawX);
      const numericY = Number(rawY);

      return [
        pinKey,
        {
          x: Number.isFinite(numericX) ? numericX : 0,
          y: Number.isFinite(numericY) ? numericY : 0,
        },
      ];
    })
  );
};

export const BUILT_IN_LED_PASSIVE = {
  id: 'builtin-led',
  componentKey: 'led',
  name: 'LED',
  description: PASSIVE_TEMPLATE_DEFAULTS.led.description,
  templateType: 'led',
  svgContent: '',
  valueOptions: [],
  defaultValue: null,
  sortOrder: 5,
  isActive: true,
  isSystem: true,
  isBuiltIn: true,
  pinOffsets: buildDefaultPassivePinOffsets('led'),
  simulationModel: buildDefaultPassiveSimulationModel('led'),
  canvasWidth: PASSIVE_TEMPLATE_DEFAULTS.led.canvasWidth,
  canvasHeight: PASSIVE_TEMPLATE_DEFAULTS.led.canvasHeight,
};

export const BUILT_IN_RESISTOR_PASSIVE = {
  id: 'builtin-resistor',
  componentKey: 'resistor',
  name: 'Resistor',
  description: PASSIVE_TEMPLATE_DEFAULTS.resistor.description,
  templateType: 'resistor',
  svgContent: `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 18" width="80" height="18">
      <line x1="0" y1="9" x2="18" y2="9" stroke="#94a3b8" stroke-width="3" stroke-linecap="round"/>
      <line x1="62" y1="9" x2="80" y2="9" stroke="#94a3b8" stroke-width="3" stroke-linecap="round"/>
      <rect x="18" y="3" width="44" height="12" rx="4" fill="#f5deb3" stroke="#92400e" stroke-width="1.2"/>
      <rect id="band_1_st" x="26" y="3.4" width="3" height="11.2" fill="#ff0000"/>
      <rect id="band_2_nd" x="34" y="3.4" width="3" height="11.2" fill="#ff0000"/>
      <rect id="band_rd_multiplier" x="42" y="3.4" width="3" height="11.2" fill="#8a3d06"/>
      <rect id="gold_band" x="53" y="3.4" width="3" height="11.2" fill="#ad9f4e"/>
      <rect id="connector0pin" x="0" y="5" width="9" height="8" fill="transparent" stroke="none"/>
      <rect id="connector1pin" x="71" y="5" width="9" height="8" fill="transparent" stroke="none"/>
    </svg>
  `,
  valueOptions: PASSIVE_TEMPLATE_DEFAULTS.resistor.valueOptions,
  defaultValue: 220,
  sortOrder: 4.9,
  isActive: true,
  isSystem: true,
  isBuiltIn: true,
  pinOffsets: buildDefaultPassivePinOffsets('resistor'),
  simulationModel: buildDefaultPassiveSimulationModel('resistor'),
  canvasWidth: PASSIVE_TEMPLATE_DEFAULTS.resistor.canvasWidth,
  canvasHeight: PASSIVE_TEMPLATE_DEFAULTS.resistor.canvasHeight,
};

export const BUILT_IN_BUZZER_PASSIVE = {
  id: 'builtin-buzzer',
  componentKey: 'buzzer',
  name: 'Buzzer',
  description: PASSIVE_TEMPLATE_DEFAULTS.buzzer.description,
  templateType: 'buzzer',
  svgContent: '',
  valueOptions: [],
  defaultValue: null,
  sortOrder: 5.5,
  isActive: true,
  isSystem: true,
  isBuiltIn: true,
  pinOffsets: buildDefaultPassivePinOffsets('buzzer'),
  simulationModel: buildDefaultPassiveSimulationModel('buzzer'),
  canvasWidth: PASSIVE_TEMPLATE_DEFAULTS.buzzer.canvasWidth,
  canvasHeight: PASSIVE_TEMPLATE_DEFAULTS.buzzer.canvasHeight,
};

export const BUILT_IN_SERVO_PASSIVE = {
  id: 'builtin-servo',
  componentKey: 'servo',
  name: 'Servo',
  description: PASSIVE_TEMPLATE_DEFAULTS.servo.description,
  templateType: 'servo',
  svgContent: '',
  valueOptions: [],
  defaultValue: null,
  sortOrder: 6,
  isActive: true,
  isSystem: true,
  isBuiltIn: true,
  pinOffsets: buildDefaultPassivePinOffsets('servo'),
  simulationModel: buildDefaultPassiveSimulationModel('servo'),
  canvasWidth: PASSIVE_TEMPLATE_DEFAULTS.servo.canvasWidth,
  canvasHeight: PASSIVE_TEMPLATE_DEFAULTS.servo.canvasHeight,
};

export const BUILT_IN_LCD_16X2_PASSIVE = {
  id: 'builtin-lcd-16x2',
  componentKey: 'lcd_16x2',
  name: 'LCD 16x2',
  description: PASSIVE_TEMPLATE_DEFAULTS.lcd_16x2.description,
  templateType: 'lcd_16x2',
  svgContent: '',
  valueOptions: [],
  defaultValue: null,
  sortOrder: 6.5,
  isActive: true,
  isSystem: true,
  isBuiltIn: true,
  pinOffsets: buildDefaultPassivePinOffsets('lcd_16x2'),
  simulationModel: buildDefaultPassiveSimulationModel('lcd_16x2'),
  canvasWidth: PASSIVE_TEMPLATE_DEFAULTS.lcd_16x2.canvasWidth,
  canvasHeight: PASSIVE_TEMPLATE_DEFAULTS.lcd_16x2.canvasHeight,
};

export const BUILT_IN_SEVEN_SEGMENT_DISPLAY_PASSIVE = {
  id: 'builtin-seven-segment-display',
  componentKey: 'seven_segment_display',
  name: '7-Segment Display',
  description: PASSIVE_TEMPLATE_DEFAULTS.seven_segment_display.description,
  templateType: 'seven_segment_display',
  svgContent: '',
  valueOptions: PASSIVE_TEMPLATE_DEFAULTS.seven_segment_display.valueOptions,
  defaultValue: PASSIVE_TEMPLATE_DEFAULTS.seven_segment_display.defaultValue,
  sortOrder: 6.75,
  isActive: true,
  isSystem: true,
  isBuiltIn: true,
  pinOffsets: buildDefaultPassivePinOffsets('seven_segment_display'),
  simulationModel: buildDefaultPassiveSimulationModel('seven_segment_display'),
  canvasWidth: PASSIVE_TEMPLATE_DEFAULTS.seven_segment_display.canvasWidth,
  canvasHeight: PASSIVE_TEMPLATE_DEFAULTS.seven_segment_display.canvasHeight,
};

export const BUILT_IN_DC_POWER_PASSIVE = {
  id: 'builtin-dc-power',
  componentKey: 'dc_power',
  name: '5 V Source',
  description: PASSIVE_TEMPLATE_DEFAULTS.dc_power.description,
  templateType: 'dc_power',
  svgContent: `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 92 64" width="92" height="64">
      <rect x="8" y="8" width="76" height="48" rx="8" fill="#f8fafc" stroke="#475569" stroke-width="2"/>
      <rect x="17" y="22" width="26" height="20" rx="4" fill="#fee2e2" stroke="#dc2626" stroke-width="1.5"/>
      <rect x="49" y="22" width="26" height="20" rx="4" fill="#e0f2fe" stroke="#2563eb" stroke-width="1.5"/>
      <text x="46" y="18" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" font-weight="700" fill="#0f172a">5V DC</text>
      <text x="30" y="36" text-anchor="middle" font-family="Arial, sans-serif" font-size="18" font-weight="900" fill="#dc2626">+</text>
      <text x="62" y="36" text-anchor="middle" font-family="Arial, sans-serif" font-size="18" font-weight="900" fill="#2563eb">-</text>
      <rect id="connector0pin" x="25" y="47" width="10" height="10" fill="transparent" stroke="none"/>
      <rect id="connector1pin" x="57" y="47" width="10" height="10" fill="transparent" stroke="none"/>
      <circle cx="30" cy="52" r="3.2" fill="#fca5a5" stroke="#b91c1c" stroke-width="1"/>
      <circle cx="62" cy="52" r="3.2" fill="#93c5fd" stroke="#1d4ed8" stroke-width="1"/>
    </svg>
  `,
  valueOptions: [],
  defaultValue: null,
  sortOrder: 5.25,
  isActive: true,
  isSystem: true,
  isBuiltIn: true,
  pinOffsets: buildDefaultPassivePinOffsets('dc_power'),
  simulationModel: buildDefaultPassiveSimulationModel('dc_power'),
  canvasWidth: PASSIVE_TEMPLATE_DEFAULTS.dc_power.canvasWidth,
  canvasHeight: PASSIVE_TEMPLATE_DEFAULTS.dc_power.canvasHeight,
};

export const BUILT_IN_BREADBOARD_PASSIVE = {
  id: 'builtin-breadboard',
  componentKey: BREADBOARD_TEMPLATE_TYPE,
  name: 'Breadboard',
  description: PASSIVE_TEMPLATE_DEFAULTS[BREADBOARD_TEMPLATE_TYPE].description,
  templateType: BREADBOARD_TEMPLATE_TYPE,
  svgContent: '',
  valueOptions: [],
  defaultValue: null,
  sortOrder: 6.9,
  isActive: true,
  isSystem: true,
  isBuiltIn: true,
  pinOffsets: buildDefaultPassivePinOffsets(BREADBOARD_TEMPLATE_TYPE),
  simulationModel: buildDefaultPassiveSimulationModel(BREADBOARD_TEMPLATE_TYPE),
  canvasWidth: PASSIVE_TEMPLATE_DEFAULTS[BREADBOARD_TEMPLATE_TYPE].canvasWidth,
  canvasHeight: PASSIVE_TEMPLATE_DEFAULTS[BREADBOARD_TEMPLATE_TYPE].canvasHeight,
};

const normalizeValueOptions = (valueOptions, templateType) => {
  if (!Array.isArray(valueOptions) || valueOptions.length === 0) {
    return PASSIVE_TEMPLATE_DEFAULTS[templateType]?.valueOptions || [];
  }

  return valueOptions
    .map((option) => {
      if (!option || typeof option !== 'object') {
        return null;
      }

      const label = String(option.label ?? option.name ?? option.value ?? '').trim();
      const rawValue = option.value ?? option.label ?? '';

      if (!label) {
        return null;
      }

      return {
        label,
        value: rawValue,
      };
    })
    .filter(Boolean);
};

export const normalizePassiveComponentDefinition = (record) => {
  if (!record || typeof record !== 'object') {
    return null;
  }

  const templateType = String(
    record.template_type ??
    record.templateType ??
    'generic_2pin'
  ).trim() || 'generic_2pin';
  const templateDefaults = PASSIVE_TEMPLATE_DEFAULTS[templateType] || PASSIVE_TEMPLATE_DEFAULTS.generic_2pin;

  return {
    id: record.id ?? '',
    componentKey: String(record.component_key ?? record.componentKey ?? '').trim(),
    name: String(record.name ?? '').trim(),
    description: String(record.description ?? templateDefaults.description ?? '').trim(),
    templateType,
    svgContent: String(record.svg_content ?? record.svgContent ?? '').trim(),
    valueOptions: normalizeValueOptions(record.value_options ?? record.valueOptions ?? [], templateType),
    defaultValue: record.default_value ?? record.defaultValue ?? templateDefaults.defaultValue ?? null,
    sortOrder: Number(record.sort_order ?? record.sortOrder ?? 0) || 0,
    isActive: record.is_active === undefined ? true : Boolean(Number(record.is_active ?? record.isActive)),
    isSystem: Boolean(Number(record.is_system ?? record.isSystem ?? 0)),
    showNoSimulationTag: Boolean(Number(
      record.show_no_simulation_tag ??
      record.showNoSimulationTag ??
      0
    )),
    pinOffsets: normalizePassivePinOffsets(record.pin_offsets ?? record.pinOffsets ?? {}, templateType),
    simulationModel: normalizePassiveSimulationModel(
      record.simulation_model ?? record.simulationModel ?? {},
      templateType
    ),
    canvasWidth: Number(record.canvas_width ?? record.canvasWidth ?? templateDefaults.canvasWidth) || templateDefaults.canvasWidth,
    canvasHeight: Number(record.canvas_height ?? record.canvasHeight ?? templateDefaults.canvasHeight) || templateDefaults.canvasHeight,
  };
};

export const getPassiveTemplateLabel = (templateType) => {
  return PASSIVE_TEMPLATE_OPTIONS.find((option) => option.value === templateType)?.label || 'Basic Part';
};

export const getPassiveTemplateDefaults = (templateType) => {
  return PASSIVE_TEMPLATE_DEFAULTS[templateType] || PASSIVE_TEMPLATE_DEFAULTS.generic_2pin;
};

export const getPassiveValueOptions = (item) => {
  const templateType = item?.templateType || item?.template_type || 'generic_2pin';
  return normalizeValueOptions(item?.valueOptions ?? item?.value_options ?? [], templateType);
};

export const passiveSupportsSelectableValues = (item) => {
  return getPassiveValueOptions(item).length > 0;
};

export const getDefaultPassiveValue = (item) => {
  const options = getPassiveValueOptions(item);
  const defaultValue = item?.defaultValue ?? item?.default_value;

  if (defaultValue !== undefined && defaultValue !== null && defaultValue !== '') {
    const matched = options.find((option) => String(option.value) === String(defaultValue));

    if (matched) {
      return matched.value;
    }
  }

  return options[0]?.value ?? '';
};

export const getPassiveSelectedValueOption = (item, selectedValue) => {
  const options = getPassiveValueOptions(item);

  if (selectedValue === undefined || selectedValue === null || selectedValue === '') {
    return options.find((option) => String(option.value) === String(getDefaultPassiveValue(item))) || null;
  }

  return options.find((option) => String(option.value) === String(selectedValue)) || null;
};

export const getPassiveSelectedValueLabel = (item, selectedValue) => {
  return getPassiveSelectedValueOption(item, selectedValue)?.label || '';
};

export const coercePassiveOhmsValue = (templateType, selectedValue, simulationModel = null) => {
  const resolvedTemplateType = String(templateType || 'generic_2pin');
  const supportsOhms = (
    ['resistor', 'potentiometer'].includes(resolvedTemplateType) ||
    (
      resolvedTemplateType === 'generic_2pin' &&
      resolveRuntimePassiveSimulationModel(
        simulationModel || {},
        resolvedTemplateType
      ).behavior === 'variable_resistor'
    )
  );

  if (!supportsOhms) {
    return undefined;
  }

  const numericValue = Number(selectedValue);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : undefined;
};

export const getPassiveInstanceName = (definition, { selectedValue, ledColorLabel } = {}) => {
  if (!definition) {
    return 'Basic Part';
  }

  if (definition.templateType === 'led') {
    return `${ledColorLabel || 'Red'} LED`;
  }

  const valueLabel = getPassiveSelectedValueLabel(definition, selectedValue);
  return valueLabel ? `${definition.name} (${valueLabel})` : definition.name;
};
